import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ApiError } from '@/lib/api-client';
import { useRequestPasswordReset } from '@/features/auth/hooks';
import { forgotPasswordSchema, type ForgotPasswordForm } from '@/schemas/password-reset';
import { AuthShell, AuthAlert, AuthButton, AuthField, AuthInput, FieldError, type AuthAlertState } from './login';

export const Route = createFileRoute('/forgot-password')({ component: ForgotPasswordPage });

export function ForgotPasswordPage() {
  const request = useRequestPasswordReset();
  const [sent, setSent] = useState(false);
  const [alert, setAlert] = useState<AuthAlertState>(null);
  const form = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema), defaultValues: { email: '' }, mode: 'onSubmit',
  });

  const onSubmit = async (values: ForgotPasswordForm) => {
    setAlert(null);
    try {
      await request.mutateAsync(values);
      setSent(true);
    } catch (err) {
      setAlert({ kind: 'error', text: err instanceof ApiError ? err.message : 'Something went wrong. Try again.' });
    }
  };

  return (
    <AuthShell>
      <h1 className="text-navy text-[34px] leading-tight m-0 mb-1.5" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
        Reset your password
      </h1>
      <p className="text-muted text-[15px] m-0 mb-[26px]">Enter your email and we'll send a reset link.</p>
      <AuthAlert alert={alert} />
      {sent ? (
        <div className="rounded-[10px] py-3 px-3.5 text-sm mb-5 border bg-state-success-bg text-state-success-text border-state-success-border">
          If an account exists for that email, we've sent a reset link. Check your inbox.
        </div>
      ) : (
        <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
          <AuthField label="Email Address" htmlFor="email" required>
            <AuthInput id="email" type="email" autoComplete="username" placeholder="name@yourcompany.com" {...form.register('email')} />
            <FieldError message={form.formState.errors.email?.message} />
          </AuthField>
          <AuthButton type="submit" disabled={request.isPending}>
            {request.isPending ? 'Sending…' : 'Send reset link'}
          </AuthButton>
        </form>
      )}
      <p className="mt-[22px] text-sm text-muted text-center m-0">
        <Link to="/login" className="text-brand-blue font-semibold no-underline hover:underline">Back to log in</Link>
      </p>
    </AuthShell>
  );
}
